// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { api } from '../../utils/api.js';

function labelToName(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
const modalStyle = {
  background: 'var(--color-surface)', borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  width: '100%', maxWidth: 440,
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const inputStyle = {
  display: 'block', width: '100%', padding: '9px 12px', fontSize: 14,
  border: '1px solid var(--color-border)', borderRadius: 8,
  background: 'var(--color-surface)', color: 'var(--color-text)',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-2)',
  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default function CustomColumnModal({ catalogId, existingColumn, onClose, onSaved, onDeleted }) {
  const editing = !!existingColumn;

  const [label, setLabel] = useState(existingColumn?.column_label || '');
  const [includeInExport, setIncludeInExport] = useState(existingColumn?.include_in_export ?? false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!label.trim()) { setError('Column name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      let result;
      if (editing) {
        result = await api.patch(`/custom-columns/${existingColumn.id}`, {
          column_label: label.trim(),
          include_in_export: includeInExport,
        });
      } else {
        result = await api.post('/custom-columns', {
          catalog_id: catalogId,
          column_label: label.trim(),
          include_in_export: includeInExport,
        });
      }
      onSaved(result.column);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!window.confirm('Delete this column and all its values? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/custom-columns/${existingColumn.id}`);
      onDeleted(existingColumn.id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {editing ? 'Edit column' : 'Add custom column'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="var(--color-text-2)" />
          </button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={labelStyle}>Column label</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onClose(); }}
              placeholder="e.g. MOQ, Lead time, Notes…"
              style={inputStyle}
              autoFocus
            />
            {label.trim() && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-text-3)' }}>
                Internal name: <code style={{ fontFamily: 'monospace' }}>{labelToName(label)}</code>
              </p>
            )}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div
                onClick={() => setIncludeInExport(v => !v)}
                style={{
                  width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer',
                  background: includeInExport ? 'var(--color-primary)' : 'var(--color-border)',
                  transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: includeInExport ? 21 : 3,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>Include in export</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-2)' }}>Column will appear in the Shopify CSV</div>
              </div>
            </label>
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-red)', fontWeight: 500 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            {editing && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ ...secondaryBtn, color: 'var(--color-red)', borderColor: 'var(--color-red)', marginRight: 'auto' }}
              >
                {deleting ? 'Deleting…' : 'Delete column'}
              </button>
            )}
            <button onClick={onClose} style={secondaryBtn}>Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving || !label.trim()}
              style={{ ...primaryBtn, opacity: (saving || !label.trim()) ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add column'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-surface)', color: 'var(--color-text-2)',
  border: '1px solid var(--color-border)', borderRadius: 8,
  padding: '9px 20px', fontWeight: 500, fontSize: 14, cursor: 'pointer',
};
